<template>
  <div class="mframe">
    <div v-if="blocked && !remote" class="mframe__notice">
      <span>{{ t('mailClient.reader.imagesBlocked') }}</span>
      <button type="button" class="mframe__show" @click="remote = true">{{ t('mailClient.reader.showImages') }}</button>
    </div>
    <iframe
      ref="frame"
      class="mframe__frame"
      :title="title || t('mailClient.reader.body')"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerpolicy="no-referrer"
      :srcdoc="doc"
      :style="{ height: `${height}px` }"
      @load="measure"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@general/ui-kit'

import { frameDocument, hasRemoteImages, textDocument } from './frame.mjs'

/**
 * A received body, drawn where it cannot touch the ERP: a sandboxed frame with
 * no scripts and a policy that blocks the network (see frame.mjs for why each
 * part is there). The frame grows to what it holds, so the page scrolls and
 * not a box inside it.
 */
const props = defineProps({
  html: { type: String, default: null },
  text: { type: String, default: '' },
  title: { type: String, default: '' },
})
const { t } = useI18n()
const { theme } = useTheme()

const frame = ref(null)
const height = ref(120)
const remote = ref(false)
let timers = []

const body = computed(() => (props.html ? props.html : textDocument(props.text)))
const blocked = computed(() => Boolean(props.html) && hasRemoteImages(props.html))
const doc = computed(() => frameDocument(body.value, { remoteImages: remote.value, scheme: theme.value === 'dark' ? 'dark' : 'light' }))

watch(doc, () => { height.value = 120 })

function measure() {
  const inner = frame.value?.contentDocument
  if (!inner?.documentElement) return
  const fit = () => {
    const next = Math.max(inner.documentElement.scrollHeight, inner.body?.scrollHeight ?? 0)
    if (next) height.value = Math.min(next + 8, 20000)
  }
  fit()
  // Images arrive after the first paint and push the text down.
  for (const image of inner.images ?? []) {
    if (!image.complete) image.addEventListener('load', fit, { once: true })
  }
  timers.forEach(clearTimeout)
  timers = [setTimeout(fit, 150), setTimeout(fit, 800)]
}

onBeforeUnmount(() => timers.forEach(clearTimeout))
</script>

<style scoped>
.mframe { display: flex; flex-direction: column; gap: var(--space-2); }
.mframe__notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--color-surface-2);
  color: var(--color-text-soft);
  font-size: var(--text-xs);
}
.mframe__show { border: 0; background: none; color: var(--color-primary); cursor: pointer; font-size: var(--text-xs); font-weight: 600; }
.mframe__frame { width: 100%; border: 0; display: block; background: transparent; }
</style>
